/**
 * ComparableCompaniesTab - Orchestrator Component
 *
 * Composes all comparable companies components for the tab view:
 * - Headers (desktop/mobile with heatmap controls and action buttons)
 * - View switching (list/heatmap/chart)
 * - Error/loader/no results states
 *
 * SELF-CONTAINED MODE: When `isActiveTab` prop is true, this component manages
 * internal effects for auto-view selection and data loading. Parent still
 * manages hook call and openedSimilarCompany state (affects parent render).
 *
 * Uses MobX observer to react to state changes for menus, toggles, and loading states.
 *
 * @module ComparableCompaniesTab
 */

import { useEffect } from 'react';
import { observer } from 'mobx-react';
import GeneralError from '../../../errors/GeneralError';
import ProfilerSearchResultsLoaderNew from '../../../../pages/search/SearchResults/components/ProfilerSearchResultsLoaderNew';
import ComparableCompaniesHeader from './ComparableCompaniesHeader';
import ComparableCompaniesHeaderMobile from './ComparableCompaniesHeaderMobile';
import ComparableCompaniesListView from './ComparableCompaniesListView';
import ComparableCompaniesHeatmapView from './ComparableCompaniesHeatmapView';
import ComparableCompaniesChartView from './ComparableCompaniesChartView';
import HeatmapLegend from '../../HeatmapLegend/HeatmapLegend';
import { UI_OPTIONS } from '../../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../../utils/constants/uiOptionKeys';

/**
 * ComparableCompaniesTab component
 *
 * @param {Object} props - Component props
 * @param {Object} props.state - Parent MobX state object (layout, observableCompany)
 * @param {Object} [props.similarCompaniesState] - Optional hook state for similar companies
 *        When provided, merges with state for child components (enables proxy getter removal)
 * @param {Object} props.project - Current project
 * @param {Object} props.profilerAccessStore - Access control store
 * @param {Object} props.profilerProjectStore - Project store with watchlist
 * @param {Object} props.utilsStore - Utils store with windowWidth
 * @param {Array} props.viewsList - List of available views (list/heatmap/chart)
 * @param {Array} props.sortOptions - Sort options for dropdown
 * @param {Object} props.comparablesSearchLoader - Loader component props
 * @param {boolean} [props.isActiveTab] - Whether this tab is currently active (enables self-contained effects)
 * @param {boolean} [props.isBackNavigation] - Whether this is a back navigation (skip initial load)
 */
const ComparableCompaniesTab = observer(
  ({
    state,
    similarCompaniesState,
    project,
    profilerAccessStore,
    profilerProjectStore,
    utilsStore,
    viewsList,
    sortOptions,
    comparablesSearchLoader = {},
    isActiveTab = false,
    isBackNavigation = false
  }) => {
    // Create a merged state accessor using Proxy to properly delegate to sources.
    // IMPORTANT: We can't use object spread ({...state, ...similarCompaniesState}) because
    // MobX computed getters are NOT enumerable and won't be included in the spread.
    // Proxy correctly handles both MobX observables (with computed getters) and plain objects.
    const mergedState = similarCompaniesState
      ? new Proxy(
          {},
          {
            get(_, prop) {
              // Properties from parent state (layout, observableCompany)
              if (prop === 'layout' || prop === 'observableCompany') {
                return state[prop];
              }
              // All other properties come from similarCompaniesState
              // This handles both direct properties and computed getters
              if (prop in similarCompaniesState) {
                return similarCompaniesState[prop];
              }
              // Fallback to state if not found in similarCompaniesState
              return state[prop];
            }
          }
        )
      : state;

    // ============================================================================
    // Self-Contained Mode Effects
    // These effects run when isActiveTab is true, moving logic from parent
    // ============================================================================

    // Effect 1: Auto-select list view on narrow mobile (chart view doesn't fit < 600px)
    useEffect(() => {
      if (!isActiveTab) return;

      const chartViewValue = viewsList[2]?.value;
      if (
        utilsStore?.windowWidth < 600 &&
        mergedState.selectedSimilarCompaniesView === chartViewValue
      ) {
        mergedState.selectSimilarCompaniesView?.(viewsList[0]?.value);
      }
    }, [
      isActiveTab,
      utilsStore?.windowWidth,
      mergedState.selectedSimilarCompaniesView,
      mergedState,
      viewsList
    ]);

    // Effect 2: Trigger initial data load when conditions are met
    useEffect(() => {
      if (!isActiveTab) return;

      const observableCompany = state.observableCompany;
      if (
        observableCompany &&
        observableCompany.revenue &&
        mergedState.similarCompaniesInitialLoad &&
        !mergedState.isLoadingSimilarCompanies &&
        !mergedState.isSimilarCompaniesLoadSuspend && // Prevent race with cache load on back nav
        !isBackNavigation // Skip on back navigation - back nav effect will handle state
      ) {
        mergedState.loadSimilarCompanies?.();
      }
    }, [
      isActiveTab,
      state.observableCompany,
      mergedState.similarCompaniesInitialLoad,
      mergedState.isLoadingSimilarCompanies,
      mergedState,
      isBackNavigation
    ]);

    // State conditions following CompanyDetails.jsx pattern
    const showError = !!mergedState.loadSimilarCompaniesError;
    const showLoader = mergedState.isLoadingSimilarCompanies && !!mergedState.loadingComparablesId;
    const showNoResults =
      !mergedState.isLoadingSimilarCompanies &&
      !mergedState.similarCompaniesInitialLoad &&
      !mergedState.loadSimilarCompaniesError &&
      !mergedState.similarCompanies?.length;
    const showResults =
      !mergedState.isLoadingSimilarCompanies &&
      !mergedState.loadSimilarCompaniesError &&
      !!mergedState.similarCompanies?.length;

    // Calculate props for HeatmapView (parent responsibility)
    const showCagr4Y =
      mergedState.profilerHeatmapColorCoding ===
      UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_COLOR_CODING]['CAGR 4Y (%)'];
    const searchCategory = UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY].Characteristics;
    // TD-2499: Lite has no EBIT — hide the EBIT-margin tooltip row and never render the chart.
    const hasExtendedAccess = profilerAccessStore.canViewProfilerDetailsExtendedData(project);

    return (
      <div
        className="similar-companies-wrap"
        style={mergedState.isSimilarCompaniesSortToggled ? { minHeight: '500px' } : {}}
      >
        {/* Error State */}
        {showError && (
          <GeneralError
            errorMessage={mergedState.loadSimilarCompaniesError}
            actionMessage="You may want to:"
            actionButtontext="Try again"
            onActionButtonClick={mergedState.loadSimilarCompanies}
          />
        )}

        {/* Loading State */}
        {showLoader && (
          <ProfilerSearchResultsLoaderNew
            wrapClassname="in-company-details-comparables"
            loaderClassname="in-company-details-comparables"
            componentProps={comparablesSearchLoader.componentProps}
          />
        )}

        {/* No Results State */}
        {showNoResults && (
          <div className="no-results">
            There are no comparable companies available for this company.
          </div>
        )}

        {/* Results State */}
        {showResults && (
          <div className="similar-listing-layout">
            <div className="results">
              {/* Mobile Header - action buttons only, CSS handles visibility */}
              <ComparableCompaniesHeaderMobile
                state={mergedState}
                viewsList={viewsList}
                sortOptions={sortOptions}
                project={project}
                profilerAccessStore={profilerAccessStore}
                utilsStore={utilsStore}
              />

              {/* Desktop Header - includes column headers and comparable data row, CSS handles visibility */}
              <ComparableCompaniesHeader
                state={mergedState}
                viewsList={viewsList}
                sortOptions={sortOptions}
                project={project}
                profilerAccessStore={profilerAccessStore}
                comparableData={mergedState.comparableData}
              />

              {/* List View */}
              {mergedState.selectedSimilarCompaniesView === viewsList[0].value && (
                <ComparableCompaniesListView
                  sortedCompanies={mergedState.sortedSimilarCompanies}
                  layout={mergedState.layout}
                  project={project}
                  profilerAccessStore={profilerAccessStore}
                  profilerProjectStore={profilerProjectStore}
                  utilsStore={utilsStore}
                  onCompanyDetailsClick={(company) =>
                    mergedState.onSimilarCompanyDetailsClick(company)
                  }
                  onWatchlistClick={(company) => profilerProjectStore.addToWatchlist(company)}
                />
              )}

              {/* Heatmap View */}
              {mergedState.selectedSimilarCompaniesView === viewsList[1].value && (
                <>
                  <ComparableCompaniesHeatmapView
                    sortedCompanies={mergedState.sortedSimilarCompanies}
                    state={mergedState}
                    showCagr4Y={showCagr4Y}
                    showEbitMargin={hasExtendedAccess}
                    searchCategory={searchCategory}
                  />
                  <HeatmapLegend colorCoding={mergedState.profilerHeatmapColorCoding} />
                </>
              )}

              {/* Chart View (Full only — EBIT-centric) */}
              {mergedState.selectedSimilarCompaniesView === viewsList[2].value && hasExtendedAccess && (
                <ComparableCompaniesChartView
                  similarCompaniesBarChart={mergedState.similarCompaniesBarChart}
                  observableCompany={mergedState.observableCompany}
                  onSimilarCompanyDetailsClick={mergedState.onSimilarCompanyDetailsClick}
                  comparablesId={mergedState.comparablesId}
                  project={project}
                  selectedSimilarCompaniesSort={mergedState.selectedSimilarCompaniesSort}
                />
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default ComparableCompaniesTab;
